import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, ArrowRight, Sparkles, CheckCircle2, ShieldCheck } from "lucide-react";

/* ─── Footer Data ─── */
const quickLinks = [
	{ label: "Pay Electricity Bill", href: "/pay-bill" },
	{ label: "Buy Prepaid Token", href: "/prepaid" },
	{ label: "Report Power Outage", href: "/customer/report-outage" },
	{ label: "New Connection Request", href: "/metering/new-customer-connection" },
	{ label: "Tariff Band Schedule", href: "/customer/band-tariff" },
	{ label: "Customer Service Centers", href: "/service-centers" },
];

const companyLinks = [
	{ label: "About Our Company", href: "/about/our-company" },
	{ label: "Board of Directors", href: "/about/board-of-directors" },
	{ label: "Executive Management", href: "/about/management-team" },
	{ label: "Area & District Offices", href: "/about/area-offices" },
	{ label: "QHSE Safety Policy", href: "/about/qhse-policy" },
	{ label: "Contact Us", href: "/customer/contact" },
];

const resourceLinks = [
	{ label: "Customer FAQs", href: "/customer/faqs" },
	{ label: "Fire & Electrical Safety Tips", href: "/customer/fire-safety-tips" },
	{ label: "MAP Meter Pricing", href: "/metering/map-prices" },
	{ label: "MAP Refund", href: "https://kadelectric.ng/self-service/map-refund" },
	{ label: "E-Payment Locations", href: "/e-payment-centers" },
	{ label: "Whistleblowing Hotline", href: "/customer/whistleblowing" },
	{ label: "Privacy Policy", href: "/legal/privacy-policy" },
];

const franchiseStates = ["Kaduna", "Kebbi", "Sokoto", "Zamfara"];

/* ─── Footer Component ─── */
const Footer: React.FC = () => {
	const currentYear = new Date().getFullYear();
	const [email, setEmail] = useState("");
	const [subscribed, setSubscribed] = useState(false);

	const handleSubscribe = (e: React.FormEvent) => {
		e.preventDefault();
		if (email.trim()) {
			setSubscribed(true);
			setEmail("");
			setTimeout(() => setSubscribed(false), 4000);
		}
	};

	return (
		<footer className="relative overflow-hidden bg-[#04150E] text-white select-none" id="footer">
			{/* Ambient emerald & teal background glow */}
			<div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent pointer-events-none" />
			<div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/[0.04] rounded-full blur-3xl pointer-events-none" />
			<div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/[0.03] rounded-full blur-3xl pointer-events-none" />

			{/* Main Content */}
			<div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20 pb-12 relative z-10">

				{/* Top Section — Brand & Newsletter */}
				<div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 pb-14 border-b border-white/[0.08]">
					{/* Brand Column (6 cols) */}
					<div className="lg:col-span-6 flex flex-col justify-between">
						<div>
							<div className="flex items-center gap-3 mb-6">
								<img
									src="/static/images/kelogo.svg"
									alt="Kaduna Electric Logo"
									className="h-10 w-auto"
								/>
							</div>
							<p className="text-[0.92rem] leading-[1.72] text-slate-300/80 max-w-[500px] font-normal mb-6">
								Kaduna Electricity Distribution Company is licensed to distribute safe, accessible, and continuous electrical power to over 0.8M+ energized consumers across North-Western Nigeria.
							</p>
						</div>

						{/* Franchise State Badges */}
						<div className="flex flex-wrap items-center gap-2">
							<span className="text-[0.72rem] font-bold uppercase tracking-wider text-slate-400 mr-1">
								Franchise Area:
							</span>
							{franchiseStates.map((state) => (
								<span
									key={state}
									className="inline-flex items-center px-3 py-1 rounded-full text-[0.74rem] font-semibold bg-white/[0.05] text-emerald-300 border border-emerald-500/20 backdrop-blur-sm"
								>
									{state}
								</span>
							))}
						</div>
					</div>

					{/* Newsletter Column (6 cols) */}
					<div className="lg:col-span-6 flex flex-col justify-between bg-white/[0.02] border border-white/[0.06] rounded-3xl p-6 sm:p-8 backdrop-blur-md">
						<div>
							<div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-[0.74rem] font-semibold mb-3">
								<Sparkles size={12} className="text-emerald-400" />
								<span>Official Dispatch Channel</span>
							</div>
							<h4 className="text-white font-extrabold text-[1.2rem] leading-tight mb-2">
								Subscribe to Grid Alerts &amp; Updates
							</h4>
							<p className="text-[0.84rem] text-slate-300/70 leading-[1.6] mb-5">
								Receive planned maintenance notices, tariff schedules, and electricity advisory alerts directly in your inbox.
							</p>
						</div>

						{/* Input Container */}
						<form onSubmit={handleSubscribe} className="relative">
							<div className="flex flex-col sm:flex-row items-center gap-2 p-1.5 rounded-2xl sm:rounded-full bg-white/[0.04] border border-white/10 focus-within:border-emerald-500/50 focus-within:bg-white/[0.06] transition-all">
								<input
									type="email"
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="Enter your corporate or personal email"
									className="w-full px-4 py-2.5 bg-transparent text-white text-[0.86rem] placeholder:text-slate-400 outline-none"
								/>
								<button
									type="submit"
									style={{ background: "linear-gradient(180deg, #10B981 0%, #059669 100%)" }}
									className="w-full sm:w-auto shrink-0 px-6 py-2.5 rounded-full text-white font-semibold text-[0.84rem] shadow-[0_4px_14px_rgba(16,185,129,0.35)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.5)] hover:opacity-95 active:scale-98 transition-all duration-200 cursor-pointer"
								>
									<span>Subscribe</span>
								</button>
							</div>
							{subscribed && (
								<p className="absolute -bottom-6 left-2 text-[0.74rem] text-emerald-400 font-semibold flex items-center gap-1 animate-fade-in-up">
									<CheckCircle2 size={12} /> Thank you! You have been subscribed.
								</p>
							)}
						</form>
					</div>
				</div>

				{/* Middle Navigation Columns */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 py-14">
					{/* Quick Links */}
					<div>
						<div className="flex items-center gap-2 mb-5">
							<span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
							<h5 className="text-white font-bold text-[0.82rem] tracking-wider uppercase">
								Quick Services
							</h5>
						</div>
						<ul className="flex flex-col gap-3">
							{quickLinks.map((link) => (
								<li key={link.label}>
									<a
										href={link.href}
										target={link.href.startsWith("http") ? "_blank" : undefined}
										rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
										className="group flex items-center text-[0.86rem] text-slate-400 hover:text-white transition-colors duration-200"
									>
										<span className="group-hover:translate-x-1 transition-transform duration-200">
											{link.label}
										</span>
									</a>
								</li>
							))}
						</ul>
					</div>

					{/* Company */}
					<div>
						<div className="flex items-center gap-2 mb-5">
							<span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
							<h5 className="text-white font-bold text-[0.82rem] tracking-wider uppercase">
								Our Company
							</h5>
						</div>
						<ul className="flex flex-col gap-3">
							{companyLinks.map((link) => (
								<li key={link.label}>
									<a
										href={link.href}
										target={link.href.startsWith("http") ? "_blank" : undefined}
										rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
										className="group flex items-center text-[0.86rem] text-slate-400 hover:text-white transition-colors duration-200"
									>
										<span className="group-hover:translate-x-1 transition-transform duration-200">
											{link.label}
										</span>
									</a>
								</li>
							))}
						</ul>
					</div>

					{/* Resources */}
					<div>
						<div className="flex items-center gap-2 mb-5">
							<span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
							<h5 className="text-white font-bold text-[0.82rem] tracking-wider uppercase">
								Help &amp; Compliance
							</h5>
						</div>
						<ul className="flex flex-col gap-3">
							{resourceLinks.map((link) => (
								<li key={link.label}>
									<a
										href={link.href}
										target={link.href.startsWith("http") ? "_blank" : undefined}
										rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
										className="group flex items-center text-[0.86rem] text-slate-400 hover:text-white transition-colors duration-200"
									>
										<span className="group-hover:translate-x-1 transition-transform duration-200">
											{link.label}
										</span>
									</a>
								</li>
							))}
						</ul>
					</div>

					{/* Contact Info */}
					<div>
						<div className="flex items-center gap-2 mb-5">
							<span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
							<h5 className="text-white font-bold text-[0.82rem] tracking-wider uppercase">
								Headquarters
							</h5>
						</div>
						<ul className="flex flex-col gap-4">
							<li className="flex items-start gap-3">
								<div
									style={{ background: "rgba(255, 255, 255, 0.05)" }}
									className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 border border-white/10 mt-0.5"
								>
									<MapPin size={14} className="text-emerald-400" />
								</div>
								<span className="text-[0.84rem] text-slate-300 leading-snug">
									1-2 Ahmadu Bello Way,<br />Kaduna State, Nigeria
								</span>
							</li>
							<li className="flex items-center gap-3">
								<div
									style={{ background: "rgba(255, 255, 255, 0.05)" }}
									className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 border border-white/10"
								>
									<Phone size={14} className="text-emerald-400" />
								</div>
								<a
									href="tel:+2349055540389"
									className="text-[0.84rem] text-slate-300 hover:text-white transition-colors duration-200 font-medium"
								>
									(+234) 905-554-0389
								</a>
							</li>
							<li className="flex items-center gap-3">
								<div
									style={{ background: "rgba(255, 255, 255, 0.05)" }}
									className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 border border-white/10"
								>
									<Mail size={14} className="text-emerald-400" />
								</div>
								<a
									href="mailto:info@kadunaelectric.com"
									className="text-[0.84rem] text-slate-300 hover:text-white transition-colors duration-200"
								>
									info@kadunaelectric.com
								</a>
							</li>
							<li className="flex items-center gap-3">
								<div
									style={{ background: "rgba(255, 255, 255, 0.05)" }}
									className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 border border-white/10"
								>
									<Clock size={14} className="text-emerald-400" />
								</div>
								<span className="text-[0.84rem] text-slate-300">
									Mon – Fri: 8:00 AM – 5:00 PM
								</span>
							</li>
						</ul>

						{/* Social Media Links */}
						<div className="flex items-center gap-2.5 mt-6">
							{["facebook", "twitter", "instagram", "linkedin"].map((platform) => (
								<a
									key={platform}
									href={`https://${platform}.com`}
									target="_blank"
									rel="noopener noreferrer"
									className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/10 flex items-center justify-center text-slate-300 hover:bg-emerald-500 hover:text-slate-950 hover:border-emerald-400 transition-all duration-300 shadow-sm"
									aria-label={platform}
								>
									<SocialIcon platform={platform} />
								</a>
							))}
						</div>
					</div>
				</div>

			</div>

			{/* Bottom Legal & Technology Bar */}
			<div className="bg-[#020B07] border-t border-white/[0.06] py-6">
				<div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
					<p className="text-[0.78rem] text-slate-400 font-medium" suppressHydrationWarning>
						© {currentYear} Kaduna Electricity Distribution Company. All rights reserved.
					</p>

					{/* Blumen Branding Badges */}
					<div className="flex flex-row items-center justify-center gap-2.5 sm:gap-3 text-[0.76rem] text-slate-400 font-medium">
						<div className="flex items-center gap-1.5">
							<span>Powered by</span>
							<img
								src="/static/images/blumen0s.png"
								alt="blumenOS"
								style={{ height: "20px", width: "auto" }}
							/>
						</div>
						<span className="text-white/20">|</span>
						<div className="flex items-center gap-1.5">
							<span>Built with <span className="text-emerald-400 font-semibold">blumenjs</span></span>
							<span className="text-white/20">|</span>
							<img
								src="/static/images/blumentech.png"
								alt="BlumenTech"
								style={{ height: "16px", width: "auto" }}
							/>
						</div>
					</div>

					{/* Legal Links */}
					<div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
						<a
							href="/legal/privacy-policy"
							className="text-[0.78rem] text-slate-400 hover:text-white transition-colors duration-200 font-medium"
						>
							Privacy Policy
						</a>
						<span className="text-white/20">|</span>
						<a
							href="/terms"
							className="text-[0.78rem] text-slate-400 hover:text-white transition-colors duration-200 font-medium"
						>
							Terms of Service
						</a>
						<span className="text-white/20">|</span>
						<a
							href="/sitemap"
							className="text-[0.78rem] text-slate-400 hover:text-white transition-colors duration-200 font-medium"
						>
							Sitemap
						</a>
					</div>
				</div>
			</div>
		</footer>
	);
};

/* ─── High-Fidelity SVG Social Icons ─── */
const SocialIcon: React.FC<{ platform: string }> = ({ platform }) => {
	const icons: Record<string, React.ReactNode> = {
		facebook: (
			<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
		),
		twitter: (
			<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
		),
		instagram: (
			<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
		),
		linkedin: (
			<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
		),
	};
	return <>{icons[platform]}</>;
};

export default Footer;
